import '../styles/GlobalDesign.css';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import PaymentForm from '../components/PaymentForm';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : process.env.REACT_APP_API_URL || '/api';

const ResellerPage = () => {
  const { slug } = useParams();
  const [reseller, setReseller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadReseller = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/resellers/public/${slug}`);
      setReseller(response.data.reseller);
      setLoading(false);
    } catch (err) {
      setError('Revendeur non trouvé');
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadReseller();
  }, [loadReseller]);

  if (loading) {
    return (
      <div className="reseller-page">
        <div className="loading">Chargement...</div>
      </div>
    );
  }

  if (error || !reseller) {
    return (
      <div className="reseller-page">
        <div className="error-container">
          <h1>❌ {error}</h1>
          <p>Cette page de revendeur n'existe pas ou a été désactivée.</p>
          <a href="/" className="back-link">← Retour à l'accueil</a>
        </div>
      </div>
    );
  }

  return (
    <div className="reseller-page public-page">
      {/* Navbar - Nouveau Design */}
      <nav className="new-design-navbar">
        <div className="new-design-container">
          <a href="/" className="new-design-logo">
            <img src="/logo.png" alt="Fluxivo" className="new-design-logo-img" style={{height: '36px', width: 'auto'}} />
            <span className="new-design-logo-text">Flux<span>ivo</span></span>
          </a>
          
          <ul className="new-design-nav-links">
            <li><a href="#accueil">Accueil</a></li>
            <li><a href="#tarifs">Tarifs</a></li>
            <li><a href="#avantages">Avantages</a></li>
            <li><a href="#contact">Contact</a></li>
          </ul>
          
          {reseller.whatsapp && (
            <a 
              href={`https://wa.me/${reseller.whatsapp.replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="new-design-btn-login"
            >
              WhatsApp
            </a>
          )}
        </div>
      </nav>

      {/* Hero Section - Nouveau Design */}
      <section 
        className="new-design-hero" 
        id="accueil"
        style={{
          background: `linear-gradient(rgba(10, 10, 20, 0.7), rgba(10, 10, 20, 0.85)), url(${process.env.PUBLIC_URL}/Fond/iptv%20image.jpg)`
        }}
      >
        <div className="new-design-hero-content">
          <h1 className="new-design-hero-title">
            Fluxivo IPTV
            <br />
            <span className="new-design-highlight">Premium</span>
          </h1>
          <p className="new-design-hero-subtitle">
            Abonnez-vous maintenant et profitez de +20,000 chaînes en streaming HD/4K avec activation immédiate
          </p>
          <div className="new-design-hero-buttons">
            <button 
              className="new-design-btn new-design-btn-primary"
              onClick={() => {
                document.querySelector('.subscription-section')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Voir les offres
            </button>
            {reseller.whatsapp && (
              <a 
                href={`https://wa.me/${reseller.whatsapp.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="new-design-btn new-design-btn-secondary"
              >
                WhatsApp
              </a>
            )}
          </div>
          <div className="new-design-hero-stats">
            <div className="new-design-stat">
              <h3>20000+</h3>
              <p>Chaînes TV</p>
            </div>
            <div className="new-design-stat">
              <h3>4K UHD</h3>
              <p>Qualité Vidéo</p>
            </div>
            <div className="new-design-stat">
              <h3>24/7</h3>
              <p>Support Client</p>
            </div>
          </div>
        </div>
      </section>

      {/* Avantages Section - Nouveau Design */}
      <section className="new-design-features" id="avantages">
        <div className="new-design-container">
          <div className="new-design-section-header">
            <h2>Pourquoi Choisir Fluxivo?</h2>
            <p>Une expérience de streaming incomparable avec des fonctionnalités premium</p>
          </div>
          
          <div className="new-design-features-grid">
            <div className="new-design-feature-item">
              <div className="new-design-feature-number">01</div>
              <h3>+20,000 Chaînes</h3>
              <p>TV, Sport, Films, Séries du monde entier en streaming HD et 4K</p>
              <img src="/images/icon-tv.svg" alt="TV" className="new-design-feature-icon-img" />
            </div>
            <div className="new-design-feature-item">
              <div className="new-design-feature-number">02</div>
              <h3>Qualité HD/4K</h3>
              <p>Streaming haute définition sans coupure avec technologie adaptive bitrate</p>
              <img src="/images/icon-video.svg" alt="Vidéo" className="new-design-feature-icon-img" />
            </div>
            <div className="new-design-feature-item">
              <div className="new-design-feature-number">03</div>
              <h3>VOD Illimité</h3>
              <p>Films et séries à la demande avec des milliers de titres disponibles</p>
              <img src="/images/icon-cinema.svg" alt="Cinéma" className="new-design-feature-icon-img" />
            </div>
            <div className="new-design-feature-item">
              <div className="new-design-feature-number">04</div>
              <h3>Support 24/7</h3>
              <p>Assistance rapide et personnalisée en français 24 heures sur 24</p>
              <img src="/images/icon-support.svg" alt="Support" className="new-design-feature-icon-img" />
            </div>
            <div className="new-design-feature-item">
              <div className="new-design-feature-number">05</div>
              <h3>Activation Instantanée</h3>
              <p>Recevez vos identifiants immédiatement après paiement</p>
              <img src="/images/icon-noad.svg" alt="Rapide" className="new-design-feature-icon-img" />
            </div>
            <div className="new-design-feature-item">
              <div className="new-design-feature-number">06</div>
              <h3>Multi-appareils</h3>
              <p>Compatible avec TV, Mobile, Tablette, PC et box Android</p>
              <img src="/images/icon-devices.svg" alt="Appareils" className="new-design-feature-icon-img" />
            </div>
          </div>
        </div>
      </section>

      {/* Plans Section - Nouveau Design */}
      <section className="new-design-pricing subscription-section" id="tarifs">
        <div className="new-design-container">
          <div className="new-design-section-header">
            <h2>Choisissez votre formule</h2>
            <p>Activation instantanée • Accès par email • Paiement sécurisé</p>
          </div>
          
          <PaymentForm resellerId={reseller.id} />

          <div className="new-design-comparison-text" style={{ marginTop: '3rem' }}>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem' }}>✅</span>
                <span>Paiement sécurisé</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem' }}>⚡</span>
                <span>Activation instantanée</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem' }}>🔒</span>
                <span>Données protégées</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Nouveau Design */}
      <footer className="new-design-footer">
        <div className="new-design-container">
          <div className="new-design-footer-grid">
            <div className="new-design-footer-section">
              <h3>Fluxivo</h3>
              <p>La plateforme IPTV la plus élégante et premium du marché.</p>
            </div>

            <div className="new-design-footer-section">
              <h4>Navigation</h4>
              <ul>
                <li><a href="#accueil">Accueil</a></li>
                <li><a href="#tarifs">Tarifs</a></li>
                <li><a href="#avantages">Avantages</a></li>
              </ul>
            </div>

            <div className="new-design-footer-section">
              <h4>Support</h4>
              <ul>
                <li><a href="mailto:support@fluxivo.com">support@fluxivo.com</a></li>
                {reseller.whatsapp && (
                  <li>
                    <a 
                      href={`https://wa.me/${reseller.whatsapp.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      WhatsApp
                    </a>
                  </li>
                )}
                <li>Support 24/7</li>
              </ul>
            </div>

            <div className="new-design-footer-section">
              <h4>Garanties</h4>
              <ul>
                <li>✓ Paiement sécurisé</li>
                <li>✓ Activation instantanée</li>
                <li>✓ Support client réactif</li>
              </ul>
            </div>
          </div>

          <div className="new-design-footer-bottom">
            <p>&copy; 2026 Fluxivo. Tous droits réservés. | Design Premium Élégant</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ResellerPage;
